import { ErrorHandler, enableProdMode, importProvidersFrom } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { Configuration as AppManagementApiConfiguration, DeviceApplicationStateService } from '@era/appmanagement-sdk';
import { LocationsHubService, LocationsMdmHubService } from '@era/core';
import {
  CancelledDeviceTaskService,
  Configuration as DeviceTaskManagementApiConfiguration,
  DeviceTaskService,
} from '@era/device-task-management-sdk';
import { Configuration as GatewayApiConfiguration, DeviceService as UnenrolledDeviceService } from '@era/gateway-sdk';
import { Configuration as InRoomApiConfiguration } from '@era/inroom-sdk';
import {
  BarService,
  DeviceActionsService,
  DeviceOperationsService,
  DevicesService,
  FloorsService,
  FrontDeskService,
  HousekeepingService,
  IntegrationIReckonHotelsAndUnitsService,
  IntegrationIReckonInRoomCredentialsService,
  IntegrationMDMDevicesService,
  IntegrationMDMPairingService,
  IntegrationMDMProxyService,
  KioskService,
  Configuration as LocationsApiConfiguration,
  LocationsService,
  RoomStatusService,
  RoomsService,
  StaffService,
  SyncCurrentlyConfiguredService,
  SyncService,
} from '@era/locations-sdk';
import {
  AvailableService,
  DeviceService,
  DevicesService as DevicesOsUpdateService,
  LocationsService as LocationOsUpdateService,
  Configuration as OsUpdatesApiConfiguration,
} from '@era/osupdates-sdk';
import { GroupsService, Configuration as SecurityApiConfiguration, UsersService } from '@era/security-sdk';
import {
  ApiClientCredentialsService,
  ApplicationSettingsLinkService,
  ApplicationSettingsService,
  ApplicationsConfigurationService,
  AutomatedConfigurationService,
  BarAppLockConfigurationService,
  BarApplicationConfigurationService,
  BarVppApplicationConfigurationService,
  BrandAirPlaySecurityConfigurationService,
  BrandApiClientCredentialsConfigurationService,
  BrandAppLockConfigurationService,
  BrandApplicationConfigurationService,
  BrandRestrictionsProfileConfigurationService,
  BrandVppApplicationConfigurationService,
  BrandWifiProfileConfigurationService,
  FrontDeskAppLockConfigurationService,
  FrontDeskApplicationConfigurationService,
  FrontDeskVppApplicationConfigurationService,
  HousekeepingAppLockConfigurationService,
  HousekeepingApplicationConfigurationService,
  HousekeepingVppApplicationConfigurationService,
  KioskAppLockConfigurationService,
  KioskApplicationConfigurationService,
  KioskRestrictionsProfileConfigurationService,
  KioskVppApplicationConfigurationService,
  KioskWifiProfileConfigurationService,
  LocationAirPlaySecurityConfigurationService,
  LocationApiClientCredentialsConfigurationService,
  LocationAppLockConfigurationService,
  LocationApplicationConfigurationService,
  LocationRestrictionsProfileConfigurationService,
  LocationVppApplicationConfigurationService,
  LocationWifiProfileConfigurationService,
  RoomAirPlaySecurityConfigurationService,
  RoomApiClientCredentialsConfigurationService,
  RoomAppLockConfigurationService,
  RoomApplicationConfigurationService,
  RoomRestrictionsProfileConfigurationService,
  RoomVppApplicationConfigurationService,
  RoomWifiProfileConfigurationService,
  Configuration as SettingsApiConfiguration,
  StaffAppLockConfigurationService,
  StaffApplicationConfigurationService,
  StaffVppApplicationConfigurationService,
  VppApplicationsConfigurationService,
} from '@era/settings-sdk';
import { MENUS, MenuModule, ProgressModule } from '@layout-lib';
import { EnvironmentModule, EnvironmentService } from '@methylium/core';
import { Configuration as MdmDepConfiguration, ServerTokenService } from '@rocksoft/mdm-dep-sdk';
import {
  AppleAirPlaySecurityProfilePresetsService,
  AppleApplicationsService,
  AppleDevicesService,
  AppleRestrictionsProfilePresetsService,
  AppleSoftwareLookupServiceService,
  AppleVppService,
  AppleWifiProfilePresetsService,
  CertificateService,
  DeviceNotesService,
  Configuration as MdmApiConfiguration,
  Configuration as MdmApiConfigurationV2,
  MdmCommandProgressService,
  MdmCommandService,
  DevicesService as MdmDevicesService,
  MdmVendorService,
  OSUpdatesService,
  VppTokenService,
} from '@rocksoft/mdm-sdk-v2';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration, replayIntegration } from '@sentry/angular';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CombinedErrorHandler } from './app/custom-error-handler';
import { FeatureGuard } from './app/feature.guard';

import { LoggingService } from './app/logging.service';
import { MenuVisibilityService } from './app/menu-visibility.service';
import { ResolverErrorLoggerProvider } from './app/resolver-error-logger.provider';
import { environment, extModules } from './environments/environment';

import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DeviceTaskManagementHubService, EraPairLibModule } from '@era/devices';
import { GroupResolver, UserResolver } from '@era/security';
import { EraStoresModule, ExportService, HttpRequestService, ScheduledApplicationsRequestService } from '@era/stores';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { ListService } from '@ml-ag-grid-lib';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { PlotlyViaCDNModule } from 'angular-plotly.js';
import { APP_ROUTES } from './app/app-routing/app.routes';
import { AppComponent } from './app/app.component';
import {
  SecurityApiConfigFactory,
  appManagementApiConfigFactory,
  deviceTaskManagementApiConfigFactory,
  gatewayApiConfigFactory,
  inRoomApiConfigFactory,
  locationsApiConfigFactory,
  mdmApiConfigFactory,
  mdmApiConfigFactoryV2,
  mdmDepConfigFactory,
  osUpdatesApiConfigFactory,
  registerIconsFactory,
  settingsApiConfigFactory,
} from './app/app.factories';
import { AuthConfigModule } from './app/auth-config.module';
import { LocationDeactivateGuard } from './app/location-deactivate.guard';
import { RoomSettingsDeactivateGuard } from './app/room-settings-deactivate.guard';
import { ToastEffect } from './app/toast.effect';

PlotlyViaCDNModule.setPlotlyVersion('1.58.4');

if (environment.sentry?.enabled && environment.sentry.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: environment.sentry.release,
    integrations: [browserTracingIntegration(), replayIntegration()],
    tracesSampleRate: environment.sentry.tracesSampleRate,
    replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
    sendClientReports: true,
  });
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      MenuModule.forRoot(),
      AuthConfigModule,
      StoreModule.forRoot({}),
      EffectsModule.forRoot([ToastEffect]),
      EraStoresModule.forRoot(),
      ProgressModule,
      EnvironmentModule.forRoot({ environment }),
      EraPairLibModule,
      PlotlyViaCDNModule,
      // Instrumentation must be imported after importing StoreModule
      extModules,
      NgbModule
    ),
    MenuVisibilityService,
    {
      provide: MENUS,
      deps: [MenuVisibilityService],
      useFactory: (service: MenuVisibilityService) => service.getMenus(),
    },
    {
      provide: ErrorHandler,
      useClass: CombinedErrorHandler,
    },
    {
      provide: MdmApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: mdmApiConfigFactory,
    },
    {
      provide: MdmApiConfigurationV2,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: mdmApiConfigFactoryV2,
    },
    {
      provide: MdmDepConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: mdmDepConfigFactory,
    },
    {
      provide: LocationsApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: locationsApiConfigFactory,
    },
    {
      provide: SecurityApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: SecurityApiConfigFactory,
    },
    {
      provide: SettingsApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: settingsApiConfigFactory,
    },
    {
      provide: InRoomApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: inRoomApiConfigFactory,
    },
    {
      provide: GatewayApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: gatewayApiConfigFactory,
    },
    {
      provide: OsUpdatesApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: osUpdatesApiConfigFactory,
    },
    {
      provide: DeviceTaskManagementApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: deviceTaskManagementApiConfigFactory,
    },
    {
      provide: LoggingService,
      deps: [EnvironmentService],
    },
    {
      provide: AppManagementApiConfiguration,
      deps: [OidcSecurityService, EnvironmentService],
      useFactory: appManagementApiConfigFactory,
    },
    FaIconLibrary,
    {
      provide: FaIconLibrary,
      useFactory: registerIconsFactory,
    },
    ResolverErrorLoggerProvider,
    AppleApplicationsService,
    AppleVppService,
    LocationsService,
    RoomsService,
    GroupsService,
    UsersService,
    FloorsService,
    RoomStatusService,
    IntegrationIReckonHotelsAndUnitsService,
    IntegrationIReckonInRoomCredentialsService,
    IntegrationMDMPairingService,
    DevicesService,
    IntegrationMDMDevicesService,
    AppleWifiProfilePresetsService,
    AppleRestrictionsProfilePresetsService,
    MdmCommandService,
    AppleDevicesService,
    AppleSoftwareLookupServiceService,
    FeatureGuard,
    LocationDeactivateGuard,
    RoomSettingsDeactivateGuard,
    LocationsHubService,
    LocationsMdmHubService,
    IntegrationMDMProxyService,
    AppleAirPlaySecurityProfilePresetsService,
    GroupResolver,
    UserResolver,
    RoomRestrictionsProfileConfigurationService,
    LocationRestrictionsProfileConfigurationService,
    BrandRestrictionsProfileConfigurationService,
    KioskRestrictionsProfileConfigurationService,
    KioskWifiProfileConfigurationService,
    RoomAirPlaySecurityConfigurationService,
    LocationAirPlaySecurityConfigurationService,
    BrandAirPlaySecurityConfigurationService,
    ApiClientCredentialsService,
    BrandApiClientCredentialsConfigurationService,
    LocationApiClientCredentialsConfigurationService,
    RoomApiClientCredentialsConfigurationService,
    BrandAppLockConfigurationService,
    LocationAppLockConfigurationService,
    RoomAppLockConfigurationService,
    BrandApplicationConfigurationService,
    LocationApplicationConfigurationService,
    RoomApplicationConfigurationService,
    BrandWifiProfileConfigurationService,
    LocationWifiProfileConfigurationService,
    RoomWifiProfileConfigurationService,
    AutomatedConfigurationService,
    AppleApplicationsService,
    AppleVppService,
    // Housekeeping
    HousekeepingService,
    HousekeepingAppLockConfigurationService,
    HousekeepingApplicationConfigurationService,
    HousekeepingVppApplicationConfigurationService,
    // Staff
    StaffService,
    StaffAppLockConfigurationService,
    StaffApplicationConfigurationService,
    StaffVppApplicationConfigurationService,
    // Front Desk
    FrontDeskService,
    FrontDeskAppLockConfigurationService,
    FrontDeskApplicationConfigurationService,
    FrontDeskVppApplicationConfigurationService,
    // Bar
    BarService,
    BarAppLockConfigurationService,
    BarApplicationConfigurationService,
    BarVppApplicationConfigurationService,
    // Kiosk
    KioskService,
    KioskAppLockConfigurationService,
    KioskApplicationConfigurationService,
    KioskVppApplicationConfigurationService,
    ExportService,
    HttpRequestService,
    ScheduledApplicationsRequestService,
    BrandVppApplicationConfigurationService,
    LocationVppApplicationConfigurationService,
    RoomVppApplicationConfigurationService,
    DeviceService,
    DevicesOsUpdateService,
    OSUpdatesService,
    LocationOsUpdateService,
    DeviceActionsService,
    DeviceOperationsService,
    MdmCommandProgressService,
    MdmDevicesService,
    UnenrolledDeviceService,
    ListService,
    VppTokenService,
    CertificateService,
    ServerTokenService,
    AvailableService,
    DeviceNotesService,
    MdmVendorService,
    ApplicationsConfigurationService,
    DeviceTaskService,
    CancelledDeviceTaskService,
    DeviceTaskManagementHubService,
    SyncCurrentlyConfiguredService,
    SyncService,
    ApplicationSettingsLinkService,
    ApplicationSettingsService,
    VppApplicationsConfigurationService,
    DeviceApplicationStateService,
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(APP_ROUTES, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
  ],
}).catch((err: unknown) => {
  console.error(err);
  if (environment.sentry?.enabled) {
    Sentry.captureException(err);
  }
});
